import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  Typography,
} from '@mui/material';
import {
  listApiScopes,
  updateApiClient,
  ApiClientListItem,
  ApiClientPatch,
  ApiClientScope,
  ApiScopeGroup,
} from '../services/apiClientService';
import ScopeTreeCheckboxes from './ScopeTreeCheckboxes';

interface Props {
  open: boolean;
  /** The client being edited (null while no row is selected). */
  client: ApiClientListItem | null;
  onClose: () => void;
  onSaved: () => void;
}

export default function EditApiClientScopesModal({ open, client, onClose, onSaved }: Props) {
  const [tree, setTree] = useState<ApiScopeGroup[]>([]);
  const [name, setName] = useState('');
  const [checked, setChecked] = useState<Set<ApiClientScope>>(new Set());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Fetch the catalog on open + pre-fill from the client's current values.
  useEffect(() => {
    if (!open || !client) return;
    setErr(null);
    setName(client.name);
    setChecked(new Set(client.scopes as ApiClientScope[]));
    listApiScopes()
      .then(setTree)
      .catch((e) => setErr(e?.message || 'Failed to load scopes'));
  }, [open, client]);

  // Send only the fields that actually changed, so a no-op save sends nothing
  // and is rejected by the backend's "at least one field" rule (422). The
  // Save button is also disabled in that case to avoid the trip.
  const nameChanged = client !== null && name.trim() !== client.name;
  const trimmedName = name.trim();
  const scopesChanged =
    client === null
      ? false
      : (() => {
          const before = new Set(client.scopes);
          if (before.size !== checked.size) return true;
          for (const s of Array.from(checked)) if (!before.has(s)) return true;
          return false;
        })();
  const dirty = nameChanged || scopesChanged;

  const save = async () => {
    if (!client) return;
    setErr(null);
    setBusy(true);
    try {
      const patch: ApiClientPatch = {};
      if (nameChanged) patch.name = trimmedName;
      if (scopesChanged) patch.scopes = Array.from(checked);
      await updateApiClient(client.id, patch);
      onClose();
      onSaved();
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Edit API Client{client ? ` — ${client.name}` : ''}</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          <Alert severity="info" sx={{ mb: 0 }}>
            Scope changes apply on the partner's next token mint. Tokens already
            issued keep their existing scopes until they expire (≤60 min). Renaming
            is admin-facing only — partners are unaffected.
          </Alert>
          <TextField
            label="Client name (visible to admins only)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            inputProps={{ maxLength: 200 }}
          />
          <Typography variant="subtitle2">Scopes</Typography>
          <ScopeTreeCheckboxes tree={tree} checked={checked} onChange={setChecked} />
          {err && <Alert severity="error">{err}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          onClick={save}
          variant="contained"
          disabled={busy || !trimmedName || checked.size === 0 || !dirty}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
